import React, { useState, useEffect, useRef } from "react";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import { format } from "date-fns";
import { Save as SaveIcon, PictureAsPdf as PdfIcon, Visibility as VisibilityIcon } from "@material-ui/icons";
import { contactExists, getDossier, getContactInfo } from "./Utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchContact } from "../../redux/actions/contactActions";
import { fetchEcheances } from "../../redux/actions/echeancesActions";
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import { numberToFormat } from "../Utils";
import Header from "../Header/Header";
import GenPdf from "../GenPdf";
import DocMenu from "../DocMenu";
import NumDoss from "../NumDoss";
import Autosuggest from "react-autosuggest";
import "../../Styles/Contrat/ContratData.scss";


const getSuggestionValue = (suggestion) => suggestion.nom;
const renderSuggestion = (suggestion) => (
  <span style={{ padding: "auto" }}>{suggestion.nom}</span>
);


const STRING_FIELDS = new Set([
  "nom", "prenom", "adresse", "ville", "cp", "mail",
  "sexe", "telp", "telf", "comp", "siret", "bank",
  "tva", "datedem", "datepgcp", "datepgce",
]);


const ContratData = () => {
  const dispatch = useDispatch();


  const devisrea = useSelector((state) => state.devis.devisrea);
  const deviscon = useSelector((state) => state.devis.deviscon);
  const dossierValue = useSelector((state) => state.devis.dossierValue);
  const contact = useSelector((state) => state.contact.items);
  const dossier = useSelector((state) => state.dossier.items);


  const [state, setState] = useState({
    currentContact: {},
    nom: "",
    prenom: "",
    idcontact: null,
    siret: "",
    adresse: "",
    sexe: "",
    ville: "",
    cp: "",
    comp: "",
    telf: "",
    telp: "",
    tva: "",
    mail: "",
    datedem: "",
    datepgcp: "",
    datepgce: "",
    bank: "",
    paymentMethod: 0,
    suggestions: [],
    isOpen: false,
    file: null,
    disableButton: false,
  });


  const updateState = (updates) => {
    const sanitized = Object.fromEntries(
      Object.entries(updates).map(([key, val]) => [
        key,
        STRING_FIELDS.has(key) ? (val ?? "") : val,
      ])
    );
    setState((prev) => ({ ...prev, ...sanitized }));
  };

  const isFirstRender = useRef(true);


  const safeFormat = (date, fmt) => {
    if (!date) return "";
    try {
      return format(new Date(date), fmt);
    } catch {
      return "";
    }
  };


  const getDossierLocal = () =>
    dossier.find((d) => d.ndossier == dossierValue) || "";

  const getDevisCon = () => {
    const arr = deviscon.filter((d) => d.ndossier == dossierValue);
    const max = Math.max(...arr.map((item) => item.iddeviscon));
    return arr.find((item) => item.iddeviscon == max) || "";
  };

  const getDevisRea = () => {
    const arr = devisrea.filter((d) => d.ndossier == dossierValue);
    const max = Math.max(...arr.map((item) => item.iddevisrea));
    return arr.find((item) => item.iddevisrea == max) || "";
  };

  const getEchNum = (pm, duree) =>
    pm == 26
      ? Math.ceil(duree / 2) + " échéances bimestrielles"
      : duree + " échéances mensuelles";

  const getEchDelais = (pm) => (pm == 26 ? 60 : 30);

  const getEchPrice = (pm, duree) =>
    pm == 26
      ? getDevisRea().totalht / Math.ceil(duree / 2)
      : ((getDevisRea().totalht + getDevisCon().totalht) * 0.66) / duree;

  const formatCategory = (dos) => {
    switch (dos.categorie_op) {
      case 1: return "I";
      case 2: return "II";
      case 3: return "III";
      default: return "";
    }
  };

  const calculateNextDueDate = (startDate, monthsToAdd) => {
    const date = new Date(startDate);
    date.setUTCMonth(date.getUTCMonth() + monthsToAdd);
    date.setDate(2);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const generateEcheances = (startDate, numberOfEcheances, intervalInMonths) => {
    const echeancesDates = [];
    const echeancesArray = [];
    for (let i = 1; i <= numberOfEcheances; i++) {
      const echeanceDate = calculateNextDueDate(startDate, i * intervalInMonths);
      const [day, month, year] = echeanceDate.split("/");
      const transformedDate = `${year}-${month}-${day}`;
      const echeance = `Echéance N° ${i}`;
      echeancesDates.push({ dateech: transformedDate, raison: echeance });
      echeancesArray.push({ echeance, date: echeanceDate });
    }
    return { echeancesDates, echeancesArray };
  };

  const genDueDate = (pm, duree) => {
    const numberOfEcheances = pm == 26 ? Math.ceil(duree / 2) : duree;
    const intervalInMonths = pm == 26 ? 2 : 1;
    return generateEcheances(state.datedem, numberOfEcheances, intervalInMonths);
  };

  const verifInfoMO = () => {
    const { currentContact, nom, prenom, adresse, ville, cp, mail, sexe, telp } = state;
    return !(
      nom === currentContact.nom &&
      prenom === currentContact.prenom &&
      adresse === currentContact.adress &&
      ville === currentContact.city &&
      cp === currentContact.cp &&
      mail === currentContact.mail &&
      sexe === currentContact.sexe &&
      telp === currentContact.tel_port
    );
  };

  const isDataEmpty = () =>
    state.datepgce === "" ||
    state.datepgcp === "" ||
    state.datedem === "" ||
    state.bank === "" ||
    state.paymentMethod == 0;


  const handleReset = () => {
    updateState({
      currentContact: {},
      idcontact: null,
      nom: "",
      prenom: "",
      siret: "",
      adresse: "",
      ville: "",
      cp: "",
      comp: "",
      telf: "",
      telp: "",
      mail: "",
      sexe: "",
      datedem: "",
      datepgcp: "",
      datepgce: "",
      tva: "",
      paymentMethod: 0,
      bank: "",
    });
  };


  const loadContratInfo = async () => {
    if (!dossierValue) return null;
    try {
      const response = await fetch(`/api/contrat/getContractInfos/${dossierValue}`);
      if (!response.ok) return null;
      const data = await response.json();
      return data[0] ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const loadData = async (dossiers, dossierVal) => {
    const currentFolder = getDossier(dossiers, dossierVal);
    const idcontact = currentFolder?.idcontact;
    const currentContact = idcontact ? getContactInfo(contact, idcontact) : null;
    const contratInfo = await loadContratInfo();

    if (contratInfo) {
      updateState({
        currentContact,
        nom: contratInfo.nom,
        prenom: contratInfo.prenom,
        idcontact: contratInfo.idcontact ?? null,
        adresse: contratInfo.adresse,
        ville: contratInfo.ville,
        cp: contratInfo.cp,
        mail: contratInfo.mail,
        sexe: contratInfo.sexe,
        telp: contratInfo.telp,
        telf: contratInfo.telf,
        comp: contratInfo.comp,
        siret: contratInfo.siret,
        bank: contratInfo.bank,
        tva: contratInfo.tva,
        paymentMethod: contratInfo.payment_method ?? 0,
        datedem: safeFormat(contratInfo.datedem, "yyyy-MM-dd"),
        datepgcp: safeFormat(contratInfo.datepgcp, "yyyy-MM-dd"),
        datepgce: safeFormat(contratInfo.datepgce, "yyyy-MM-dd"),
      });
    } else if (currentContact) {
      updateState({
        currentContact,
        nom: currentContact.nom,
        prenom: currentContact.prenom,
        idcontact: currentContact.idcontact ?? null,
        adresse: currentContact.adress,
        ville: currentContact.city,
        cp: currentContact.cp,
        mail: currentContact.mail,
        sexe: currentContact.sexe,
        telp: currentContact.tel_port,
        comp: currentFolder?.sci_sccv,
        telf: "",
        siret: "",
        tva: "",
        datedem: "",
        datepgcp: "",
        datepgce: "",
        bank: "",
        paymentMethod: getDevisCon()?.payment_method || 0,
      });
    } else {
      handleReset();
    }
  };


  useEffect(() => {
    dispatch(changePdfFileName(""));
    if (dossierValue !== "") {
      if (!isFirstRender.current) {
        updateState({ file: null });
      }
      loadData(dossier, dossierValue);
    }
    isFirstRender.current = false;
  }, [dossierValue]);


  const handleChange = (event) => {
    const { name, value } = event.target;
    updateState({ [name]: value });
    if (name === "nom" && !contactExists(value, contact)) {
      updateState({
        currentContact: {},
        idcontact: null,
        siret: "",
        prenom: "",
        adresse: "",
        ville: "",
        cp: "",
        comp: "",
        telf: "",
        telp: "",
        mail: "",
        sexe: "",
        datedem: "",
        datepgcp: "",
        datepgce: "",
        paymentMethod: 0,
      });
    }
  };


  const getSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    return inputLength === 0
      ? []
      : contact.filter(
        (item) =>
          item.nom.toLowerCase().slice(0, inputLength) === inputValue &&
          item.fonction === "Maitre d'ouvrage"
      );
  };

  const onSuggestionsFetchRequested = ({ value }) =>
    updateState({ suggestions: getSuggestions(value) });

  const onSuggestionsClearRequested = () =>
    updateState({ suggestions: [] });

  const onSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    updateState({
      idcontact: suggestion.idcontact,
      siret: suggestion.siret,
      nom: suggestionValue,
      prenom: suggestion.prenom,
      adresse: suggestion.adress,
      ville: suggestion.city,
      cp: suggestion.cp,
      comp: suggestion.entreprise,
      telp: suggestion.tel_port,
      mail: suggestion.mail,
      sexe: suggestion.sexe,
    });
  };


  const getFile = async (type) => {
    try {
      const res = await fetch("/api/contrat/showPDF", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ndossier: dossierValue, type }),
      });
      const blob = await res.blob();
      const fileName = res.headers.get("X-FileName");
      return { blob, fileName };
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const handleGetPdfFile = async () => {
    updateState({ file: null });
    const response = await getFile("pdf");
    if (response?.blob.size > 100) {
      updateState({ file: response.blob });
      dispatch(changePdfFileName(response.fileName));
    }
  };

  const handleGetWordFile = async () => {
    updateState({ file: null });
    const response = await getFile("word");
    if (
      response?.blob.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      updateState({ file: response.blob });
      dispatch(changePdfFileName(response.fileName));
    }
  };

  const handleGetContrat = async () => {
    updateState({ file: null, disableButton: true });
    const response = await getFile("pdf");
    if (response?.blob.size > 100) {
      updateState({ file: response.blob, isOpen: true, disableButton: false });
      dispatch(changePdfFileName(response.fileName));
    } else {
      updateState({ disableButton: false });
    }
  };

  const handleChangeTitleName = () => dispatch(changeTitleName("Contrat"));


  const makeRequestOptions = (body) => ({
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

  const buildJsonPayloads = () => {
    const { paymentMethod } = state;
    const json = {};
    const jsonContact = {};
    const jsonEcheances = {};

    jsonContact.idcontact = state.idcontact;
    jsonContact.nom = state.nom.toUpperCase();
    jsonContact.prenom = state.prenom;
    jsonContact.sexe = state.sexe;
    jsonContact.adress = state.adresse;
    jsonContact.city = state.ville.toUpperCase();
    jsonContact.cp = state.cp;
    jsonContact.mail = state.mail;
    jsonContact.tel_port = state.telp;

    json.payment_method = paymentMethod;
    json.devis_rea = getDevisRea();
    json.dossier = getDossierLocal();
    json.siret = state.siret;
    json.tva = state.tva;
    json.adresse_mo = state.adresse;
    json.ville_mo = state.ville.toUpperCase();
    json.cp_mo = state.cp;
    json.sci_sccv = state.comp.toUpperCase();
    json.tel_fixe = state.telf || "";
    json.tel_port = state.telp;
    json.mail_mo = state.mail;
    json.nom_mo = state.nom.toUpperCase();
    json.prenom_mo = state.prenom;
    json.sexe_mo = state.sexe;
    json.datedem = state.datedem;
    json.datepgcp = state.datepgcp;
    json.datepgce = state.datepgce;
    json.bank_name = state.bank;

    const duree = json.devis_rea.duree;
    const echeances = genDueDate(paymentMethod, duree);
    json.due_date = echeances.echeancesArray;

    const today = new Date().toISOString().slice(0, 10);
    jsonEcheances.ndossier = json.dossier.ndossier;
    jsonEcheances.datepgcp = state.datepgcp;
    jsonEcheances.datepgce = state.datepgce;
    jsonEcheances.datedem = state.datedem;
    jsonEcheances.dates = echeances.echeancesDates;
    jsonEcheances.payment_method = paymentMethod;
    jsonEcheances.amendment = 1;
    jsonEcheances.created_date = today;

    return { json, jsonContact, jsonEcheances, duree };
  };


  const handleGenClick = async () => {
    updateState({ file: null, disableButton: true });
    const { paymentMethod } = state;
    const { json, jsonContact, jsonEcheances, duree } = buildJsonPayloads();

    json.tva = state.tva;
    json.category = formatCategory(json.dossier);
    json.dossier.operation_maj = json.dossier.operation.toUpperCase();
    json.dossier.ndossier_ = json.dossier.ndossier.toString().split("").join("-");
    json.year_1 = new Date().getFullYear() - 1;
    json.opprice = (json.dossier.cout_op * 1000000).toLocaleString();
    json.devis_con = getDevisCon();
    json.nprop = json.devis_con.nprop.toString().length === 1
      ? "0" + json.devis_con.nprop
      : json.devis_con.nprop;
    json.idcontact = state.idcontact;
    json.mail_mo2 = "";
    json.sexe = state.sexe === "homme" ? "Monsieur" : "Madame";

    const devisConTotalht = getDevisCon().totalht;
    const devisReaTotalht = getDevisRea().totalht;
    json.totalht_con = numberToFormat(devisConTotalht);
    json.totalttc_con = numberToFormat(devisConTotalht * 1.2);
    json.totalht_rea = numberToFormat(devisReaTotalht);
    json.totalttc_rea = numberToFormat(devisReaTotalht * 1.2);
    json.totalht = numberToFormat(Number(devisConTotalht) + Number(devisReaTotalht));
    json.totalttc = numberToFormat(Number(devisConTotalht * 1.2) + Number(devisReaTotalht * 1.2));

    if (paymentMethod == 26) {
      json.totalht_con26 = numberToFormat(devisConTotalht * 0.26);
      json.totalttc_con26 = numberToFormat(devisConTotalht * 0.26 * 1.2);
      json.totalht_con74 = numberToFormat(devisConTotalht * 0.74);
      json.totalttc_con74 = numberToFormat(devisConTotalht * 0.74 * 1.2);
    } else {
      json.slice34 = numberToFormat((devisConTotalht + devisReaTotalht) * 0.34);
    }

    json.echnum = getEchNum(paymentMethod, duree);
    json.delai = getEchDelais(paymentMethod);
    json.echprice_ht = numberToFormat(getEchPrice(paymentMethod, duree));
    json.echprice_ttc = numberToFormat(getEchPrice(paymentMethod, duree) * 1.2);

    const blob = await fetch("/api/contrat/genPDF", makeRequestOptions(json)).then((r) => r.blob());
    if (blob) {
      updateState({ file: blob, isOpen: true });
      await fetch("/api/contrat/registerEchContrat", makeRequestOptions(jsonEcheances)).catch(console.error);
      if (verifInfoMO() && state.idcontact !== null) {
        await fetch("/api/contact/updateContact", makeRequestOptions(jsonContact)).catch(console.error);
        await dispatch(fetchContact());
      }
      await fetch("/api/contrat/saveContrat", makeRequestOptions(json)).catch(console.error);
      await dispatch(fetchEcheances());
      updateState({ disableButton: false });
    }
  };


  const handleSaveClick = async () => {
    updateState({ disableButton: true });
    const { json, jsonContact, jsonEcheances } = buildJsonPayloads();

    await fetch("/api/contrat/saveContrat", makeRequestOptions(json)).catch(console.error);
    await fetch("/api/contrat/registerEchContrat", makeRequestOptions(jsonEcheances)).catch(console.error);
    if (verifInfoMO() && state.idcontact !== null) {
      await fetch("/api/contact/updateContact", makeRequestOptions(jsonContact)).catch(console.error);
      await dispatch(fetchContact());
    }
    await dispatch(fetchEcheances());
    updateState({ disableButton: false });
  };


  const inputProps = {
    name: "nom",
    value: state.nom,
    onChange: handleChange,
  };

  return (
    <div className="contratData">
      <Header />
      <DocMenu
        file={state.file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <NumDoss />
      <h1 className="title">Données du contrat</h1>
      <div className="data">
        <div className="table-data">
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead className="table-head">
                <TableRow className="head-row">
                  <TableCell style={{ padding: "20px" }} colSpan={2}>
                    CONTRAT
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Nom Maître d'Ouvrage</TableCell>
                  <TableCell>
                    <Autosuggest
                      suggestions={state.suggestions}
                      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                      onSuggestionsClearRequested={onSuggestionsClearRequested}
                      onSuggestionSelected={onSuggestionSelected}
                      getSuggestionValue={getSuggestionValue}
                      renderSuggestion={renderSuggestion}
                      inputProps={inputProps}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Prénom Maître d'Ouvrage</TableCell>
                  <TableCell>
                    <input type="text" name="prenom" value={state.prenom} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Genre</TableCell>
                  <TableCell>
                    <select name="sexe" value={state.sexe} onChange={handleChange}>
                      <option value=""></option>
                      <option value="homme">Homme</option>
                      <option value="femme">Femme</option>
                    </select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Adresse</TableCell>
                  <TableCell>
                    <input type="text" name="adresse" value={state.adresse} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Ville</TableCell>
                  <TableCell>
                    <input type="text" name="ville" value={state.ville} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Code Postal</TableCell>
                  <TableCell>
                    <input type="text" name="cp" value={state.cp} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Téléphone fixe</TableCell>
                  <TableCell>
                    <input type="text" name="telf" value={state.telf} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Téléphone Portable</TableCell>
                  <TableCell>
                    <input type="text" name="telp" value={state.telp} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Mail</TableCell>
                  <TableCell>
                    <input type="text" name="mail" value={state.mail} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Entreprise</TableCell>
                  <TableCell>
                    <input type="text" name="comp" value={state.comp} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SIRET</TableCell>
                  <TableCell>
                    <input type="text" name="siret" value={state.siret} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Démarrage du chantier</TableCell>
                  <TableCell>
                    <input type="date" name="datedem" value={state.datedem} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Paiement 1ere tranche (26% ou 34%)</TableCell>
                  <TableCell>
                    <input type="date" name="datepgcp" value={state.datepgcp} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Envoi du PGC</TableCell>
                  <TableCell>
                    <input type="date" name="datepgce" value={state.datepgce} onChange={handleChange} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Banque</TableCell>
                  <TableCell>
                    <select name="bank" value={state.bank} onChange={handleChange}>
                      <option value=""></option>
                      <option value="bred">BRED</option>
                      <option value="ca">CREDIT AGRICOLE</option>
                    </select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Type de paiement</TableCell>
                  <TableCell>
                    <span>{state.paymentMethod}%</span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>TVA intracommunautaire</TableCell>
                  <TableCell>
                    <input type="text" name="tva" value={state.tva} onChange={handleChange} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <div className="subtable-btn">
            {state.disableButton && (
              <CircularProgress className="spinner" color="inherit" />
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              className="btn-valid"
              onClick={handleSaveClick}
              disabled={isDataEmpty() || state.disableButton}
            >
              Enregistrer
            </Button>
            <Button
              variant="contained"
              className="btn-valid"
              startIcon={<PdfIcon />}
              onClick={handleGenClick}
              disabled={isDataEmpty() || state.disableButton}
            >
              Générer
            </Button>
            <Button
              variant="contained"
              className="btn-valid"
              startIcon={<VisibilityIcon />}
              onClick={handleGetContrat}
              disabled={state.disableButton}
            >
              Afficher
            </Button>
            <Button
              variant="contained"
              className="btn-reset"
              onClick={handleReset}
              disabled={state.disableButton}
            >
              Annuler
            </Button>
          </div>
        </div>

        <GenPdf
          isOpen={state.isOpen}
          file={state.file}
          closeDoc={() => updateState({ isOpen: false })}
          scale={state.file !== null ? 1 : 0.5}
          type={"contrat"}
        />
      </div>
    </div>
  );
};

export default ContratData;
